/**
 * Codec base64 para campos de "código" que contêm `<script>` (Head & Footer, etc.).
 *
 * POR QUÊ: o servidor está atrás do WAF da Cloudflare, que BLOQUEIA (403) qualquer requisição
 * cujo corpo contenha `<script>`/markup XSS. Por isso o admin não conseguia SALVAR código com
 * script. O campo custom (`ScriptCodeField`) codifica o valor em base64 ANTES de o formulário
 * enviar — o WAF só vê `b64:PHNjcmlwdD4…` (alfanumérico, sem `<script>`) e deixa passar. Nós
 * DECODIFICAMOS no ponto único de leitura (`HeadFooterCode/get.ts`), então o conteúdo armazenado
 * e renderizado continua sendo o HTML/script cru. Ver `project_waf_bloqueia_script_save_import`.
 *
 * Isomórfico (Node + browser) e UTF-8 safe. Idempotente e com degradação segura: se algo falhar,
 * devolve a string como está. Valores LEGADOS (gravados crus, sem o prefixo) passam intactos no
 * decode — então o GA4 já posto no banco continua funcionando durante a transição.
 */

const PREFIX = 'b64:'

/** Codifica para `b64:<base64>`. Vazio → ''. Já-codificado → devolve igual (idempotente). */
export function encodeScriptField(raw: string | null | undefined): string {
  const s = typeof raw === 'string' ? raw : ''
  if (!s) return ''
  if (s.startsWith(PREFIX)) return s
  try {
    if (typeof Buffer !== 'undefined') {
      return PREFIX + Buffer.from(s, 'utf8').toString('base64')
    }
    const bytes = new TextEncoder().encode(s)
    let bin = ''
    for (let i = 0; i < bytes.length; i++) bin += String.fromCharCode(bytes[i])
    return PREFIX + btoa(bin)
  } catch {
    return s
  }
}

/** Decodifica `b64:<base64>` → cru. Vazio ou SEM prefixo (legado cru) → devolve como está. */
export function decodeScriptField(stored: string | null | undefined): string {
  const s = typeof stored === 'string' ? stored : ''
  if (!s || !s.startsWith(PREFIX)) return s
  const b64 = s.slice(PREFIX.length)
  try {
    if (typeof Buffer !== 'undefined') {
      return Buffer.from(b64, 'base64').toString('utf8')
    }
    const bin = atob(b64)
    const bytes = Uint8Array.from(bin, (c) => c.charCodeAt(0))
    return new TextDecoder().decode(bytes)
  } catch {
    return s
  }
}

export const SCRIPT_FIELD_B64_PREFIX = PREFIX
